import { ReactElement, ReactNode, useState } from 'react';

import { Button, ButtonProps } from '@components/button';
import { IconProps } from '@components/icons';
import { Modal, hideModal, useModal } from '@components/modal';
import { ModalVariant, SimpleHeaderProps } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

export type ConfirmModalProps = {
  title: SimpleHeaderProps['title'];
  headerIcon?: SimpleHeaderProps['icon'];
  onConfirm(): void;
  description?: string;
  children?: ReactNode;
  buttonText?: string;
  buttonIcon?: ReactElement<IconProps>;
  buttonVariant?: ButtonProps['variant'];
  hideButton?: boolean;
};

export const useConfirmModal = (variant?: ModalVariant) =>
  useModal(ConfirmModal, { variant: variant ?? 'REGULAR' });

const ConfirmModal = ({
  title,
  headerIcon,
  onConfirm,
  description,
  children,
  buttonText,
  buttonIcon,
  buttonVariant = 'primary',
  hideButton = false,
}: ConfirmModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      hideModal();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={title} icon={headerIcon} />
      <Modal.Content>
        <Stack space={6}>
          {description && <Text text={description} />}
          {children}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          {!hideButton && (
            <Button
              onClick={handleConfirm}
              disabled={isLoading}
              variant={buttonVariant}
              iconLeft={buttonIcon ?? <></>}
              text={buttonText ?? t('submit')}
              dataTestid="confirm-modal-primary-button"
            />
          )}
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    submit: 'Submit',
  },
};
